import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInactivityLock } from '@/hooks/useInactivityLock';
import { LockScreen } from '@/components/LockScreen';
import { LockProvider } from '@/contexts/LockContext';
import { useTenant } from '@/contexts/TenantContext';
import { OnboardingWizard } from '@/components/OnboardingWizard';
import { useSubscriptionStore } from '@/store/subscriptionStore';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { MaintenanceScreen } from '@/components/MaintenanceScreen';
import { supabase } from '@/integrations/supabase/client';
import { TwoFactorVerify } from '@/components/mfa/TwoFactorVerify';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, profile } = useAuth();
  const { tenant, refreshTenant } = useTenant();
  const { initialize, isInitialized } = useSubscriptionStore();
  const { isLocked, hasPin, remainingTime, unlock, lockNow } = useInactivityLock(user?.id || null);
  const { isMaintenanceMode, maintenanceMessage, isLoading: maintenanceLoading } = useMaintenanceMode();
  const [needs2FA, setNeeds2FA] = useState(false);
  const [checking2FA, setChecking2FA] = useState(false);

  // Initialize subscription store when tenant is available
  useEffect(() => {
    if (tenant?.id && !isInitialized) {
      initialize(tenant.id);
    }
  }, [tenant?.id, isInitialized, initialize]);

  // Enforce 2FA for super_admin
  useEffect(() => {
    if (!user || !profile || profile.role !== 'super_admin') return;

    const checkAAL = async () => {
      setChecking2FA(true);
      try {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (data && data.nextLevel === 'aal2' && data.currentLevel !== 'aal2') {
          setNeeds2FA(true);
        } else {
          setNeeds2FA(false);
        }
      } catch {
        setNeeds2FA(false);
      }
      setChecking2FA(false);
    };

    checkAAL();
  }, [user, profile]);

  if (loading || maintenanceLoading || checking2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check maintenance mode - super_admin can bypass
  if (isMaintenanceMode && profile?.role !== 'super_admin') {
    return (
      <MaintenanceScreen
        message={maintenanceMessage}
        tenantName={tenant?.business_name}
      />
    );
  }

  // Enforce 2FA for super_admin with enrolled factors
  if (needs2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <TwoFactorVerify
            onVerified={() => setNeeds2FA(false)}
            onCancel={async () => {
              await supabase.auth.signOut();
            }}
          />
        </div>
      </div>
    );
  }

  // Onboarding wizard for new tenants
  if (tenant && tenant.is_onboarded === false && profile) {
    return (
      <OnboardingWizard
        tenant={tenant}
        profile={profile}
        onComplete={async () => { await refreshTenant(); }}
      />
    );
  }

  return (
    <LockProvider hasPin={hasPin} remainingTime={remainingTime} lockNow={lockNow}>
      {isLocked && hasPin && user && (
        <LockScreen userId={user.id} onUnlock={unlock} />
      )}
      {children}
    </LockProvider>
  );
}